import { bytesToHex } from '@stacks/common';
import { ensureLegacyBtcAddressForPox1, StackingClient } from '@stacks/stacking';
import { deserializeTransaction, MultiSigSpendingCondition } from '@stacks/transactions';
import {
  createSafeTransaction,
  executeTransaction,
  revokeTransaction,
  saveSignedTransaction,
} from 'api';
import { NotificationCard } from 'components/NotificationCard';
import { toast } from 'sonner';

import { NETWORK } from '../types/enums';
import { SAFE_TX_SUCCESS, SafeTxError, safeTxError, SafeTxResponse } from '../types/SafeTxResponse';
import {
  generateSendBatchNft,
  generateSendNft,
  generateSendTokenTransfer,
  generateUnsignedContractCall,
  generateUnsignedStxTransfer,
} from '../utils/transaction-create';

import useNetwork from './use-network';
import useSafe from './use-safe';
import useSign from './use-sign';
import { useWalletProvider } from './useWalletProvider';

// TODO_Y: refactor this hook
export const useSafeCalls = () => {
  const [network, stacksNetwork] = useNetwork();
  const { safe, fetchSafeData } = useSafe();
  const walletProvider = useWalletProvider();
  const { signTx } = useSign();

  const reloadTransactions = async () => {
    fetchSafeData(safe._id);
  };

  const nativeSignTx = async (
    transactionId: string,
    transactionHex: string,
    isFirstSignature?: boolean
  ): Promise<SafeTxResponse> => {
    if (!walletProvider) return safeTxError(SafeTxError.UnspecifiedError);
    const initialTx = deserializeTransaction(transactionHex);
    const afterSign = async (signedTx: string) => {
      const parsedSignedTx = deserializeTransaction(signedTx);
      const hasFeeChanged =
        initialTx?.auth.spendingCondition.fee !== parsedSignedTx.auth.spendingCondition.fee;

      if (!isFirstSignature && hasFeeChanged) {
        return safeTxError(SafeTxError.FeeHasChanged);
      }
      // TODO: need to consider response and return true for success and false for failure
      await saveSignedTransaction(transactionId, signedTx);
      reloadTransactions();

      return SAFE_TX_SUCCESS;
    };

    return signTx(transactionHex, afterSign);
  };

  const nativeRevokeTxSignature = async (transactionId: string, transactionHex: string) => {
    const transaction = deserializeTransaction(transactionHex);
    const authFields = (transaction.auth.spendingCondition as MultiSigSpendingCondition).fields;
    authFields.splice(authFields.length - 1, 1);
    const serializedTx = bytesToHex(transaction.serialize());

    await revokeTransaction(transactionId, serializedTx);

    reloadTransactions();
  };

  const nativeExecuteTx = async (transactionId: string) => {
    await executeTransaction(transactionId);

    reloadTransactions();
  };

  const nativeSaveTX = async (
    rawTransaction: string,
    notesValue?: string
  ): Promise<SafeTxResponse> => {
    const afterSign = async (signedTx?: string) => {
      const response = await createSafeTransaction(safe._id, rawTransaction, signedTx, notesValue);

      reloadTransactions();
      toast(
        () => (
          <NotificationCard
            title="Transaction signed"
            message="Transaction created and signed successfully"
            type="success"
          />
        ),
        {
          className: 'w-full',
        }
      );

      if (response.success) return SAFE_TX_SUCCESS;

      return safeTxError(SafeTxError.UnspecifiedError);
    };

    return await signTx(rawTransaction, afterSign);
  };

  const nativeSendSTX20 = async (
    nonce: number,
    recipient: string,
    memo: string,
    notesValue: string
  ): Promise<SafeTxResponse> => {
    try {
      const transaction = await generateUnsignedStxTransfer(
        nonce,
        recipient,
        1,
        stacksNetwork,
        safe.owners.map((owner) => owner.publicKey),
        safe.threshold,
        safe.address,
        0,
        memo
      );

      return await nativeSaveTX(transaction, notesValue);
    } catch (e) {
      console.error(e);

      return safeTxError(SafeTxError.UnspecifiedError);
    }
  };

  const nativeSendSTX = async (
    nonce: number,
    recipient: string,
    amount: number,
    fee: number,
    memo: string,
    notesValue: string
  ): Promise<SafeTxResponse> => {
    try {
      const transaction = await generateUnsignedStxTransfer(
        nonce,
        recipient,
        amount,
        stacksNetwork,
        safe.owners.map((o) => o.publicKey),
        safe.threshold,
        safe.address,
        fee,
        memo
      );

      return await nativeSaveTX(transaction, notesValue);
    } catch (e) {
      console.error(e);

      return safeTxError(SafeTxError.UnspecifiedError);
    }
  };

  const nativeSendToken = async (
    nonce: number,
    ft: string,
    amount: number,
    recipient: string,
    fee: number,
    ref: string,
    memo: string,
    notesValue: string
  ): Promise<SafeTxResponse> => {
    try {
      const transaction = await generateSendTokenTransfer(
        nonce,
        ft,
        amount,
        recipient,
        safe,
        stacksNetwork,
        ref,
        fee,
        memo
      );

      return await nativeSaveTX(transaction, notesValue);
    } catch (e) {
      console.error(e);

      return safeTxError(SafeTxError.UnspecifiedError);
    }
  };

  const nativeGetStakingInfo = async () => {
    const stackingClient = new StackingClient(safe.address, stacksNetwork);
    const poxInfo = await stackingClient.getPoxInfo();

    return poxInfo;
  };

  const nativeStackSTX = async (
    nonce: number,
    amountMicroStx: number,
    poxAddress: string,
    cycles: number,
    burnBlockHeight: number,
    notesValue: string
  ) => {
    const stackingClient = new StackingClient(safe.address, stacksNetwork);
    const poxInfo = await stackingClient.getPoxInfo();
    const poxOperatorInfo = await stackingClient.getPoxOperationInfo(poxInfo);
    const contract = await stackingClient.getStackingContract(poxOperatorInfo);
    ensureLegacyBtcAddressForPox1({ contract, poxAddress });
    const callOptions = stackingClient.getStackOptions({
      amountMicroStx,
      cycles,
      poxAddress,
      contract,
      burnBlockHeight,
    });

    await nativeSaveTX(
      await generateUnsignedContractCall(
        nonce,
        callOptions.contractAddress,
        callOptions.contractName,
        callOptions.functionName,
        callOptions.functionArgs,
        stacksNetwork,
        safe.owners.map((o) => o.publicKey),
        safe.threshold
      ),
      notesValue
    );
  };

  const nativeSendNFT = async (
    nonce: number,
    nft: string,
    id: number,
    recipient: string,
    ref: string,
    fee: number,
    notesValue: string
  ): Promise<SafeTxResponse> => {
    return await nativeSaveTX(
      await generateSendNft(nonce, nft, id, recipient, stacksNetwork, safe, ref, fee),
      notesValue
    );
  };

  const nativeSendBatchNFT = async (
    nonce: number,
    nft: string,
    ids: number[],
    recipient: string,
    ref: string,
    fee: number,
    notesValue: string
  ): Promise<SafeTxResponse> => {
    return await nativeSaveTX(
      await generateSendBatchNft(
        nonce,
        nft,
        ids,
        recipient,
        network as NETWORK,
        stacksNetwork,
        safe,
        ref,
        fee
      ),
      notesValue
    );
  };

  return {
    nativeSendSTX,
    nativeSendToken,
    nativeSignTx,
    nativeExecuteTx,
    nativeRevokeTxSignature,
    nativeGetStakingInfo,
    nativeStackSTX,
    nativeSendNFT,
    nativeSendBatchNFT,
    nativeSendSTX20,
  };
};
